import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFiscalPeriod } from '../contexts/FiscalPeriodContext';
import { useModule } from '../contexts/ModuleContext';
import { getRecycleBinItems, getRecycleBinStats } from '../api/recycleBin';
import RestoreModal from '../components/RestoreModal';
import PermanentDeleteModal from '../components/PermanentDeleteModal';
import {
  Trash2,
  RotateCcw,
  Search,
  Filter,
  BarChart3
} from 'lucide-react';

const RecycleBinPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filters, setFilters] = useState({
    entity_type: '',
    search: '',
    deleted_by: '',
    start_date: '',
    end_date: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  
  const { user } = useAuth();
  const location = useLocation();
  const { activeModule } = useModule();
  const { module: contextModule } = useFiscalPeriod();
  
  // Determine effective module based on:
  // 1. URL path (strongest signal)
  // 2. Active module from global context
  // 3. Context module from fiscal provider
  // 4. Default to 'retail'
  const effectiveModule = location.pathname.toLowerCase().includes('/wholesale') 
    ? 'wholesale' 
    : (activeModule === 'wholesale' ? 'wholesale' : (contextModule || 'retail'));
  
  const limit = 20;

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, [filters, currentPage, effectiveModule]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        ...filters,
        module: effectiveModule,
        limit,
        offset: (currentPage - 1) * limit
      };

      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await getRecycleBinItems(params);
      setItems(response.items || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch recycle bin items');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getRecycleBinStats({ module: effectiveModule });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleRestore = (item) => {
    setSelectedItem(item);
    setShowRestoreModal(true);
  };

  const handlePermanentDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleSuccess = () => {
    fetchItems();
    fetchStats();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEntityType = (type) => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Extract entity name from entity_data based on entity type
  const getEntityName = (item) => {
    const data = item.entity_data;
    if (!data) return `ID: ${item.entity_id}`;
    
    // Handle different entity types
    switch (item.entity_type) {
      case 'products':
        return data.name || data.product_code || `Product #${item.entity_id}`;
      case 'customers':
        return data.full_name || data.name || data.phone || `Customer #${item.entity_id}`;
      case 'referrers':
        return data.full_name || data.name || `Referrer #${item.entity_id}`;
      case 'users':
        return data.full_name || data.email || `User #${item.entity_id}`;
      case 'sales':
        return data.receipt_number || `Sale #${item.entity_id}`;
      case 'expenses':
        return data.expense_number || data.description || `Expense #${item.entity_id}`;
      case 'brands':
      case 'sizes':
      case 'tile_types':
      case 'use_cases':
      case 'expense_categories':
      case 'accessory_categories':
        return data.name || data.dimension || `Category #${item.entity_id}`;
      case 'vendors':
        return data.name || data.company_name || `Vendor #${item.entity_id}`;
      case 'bills':
        return data.bill_number || `Bill #${item.entity_id}`;
      case 'customer_invoices':
        return data.invoice_number || `Invoice #${item.entity_id}`;
      case 'journal_entries':
        return data.entry_number || data.description || `Entry #${item.entity_id}`;
      case 'wholesale_order':
        return data.order_number || `Order #${item.entity_id}`;
      case 'wholesale_distributor':
        return data.business_name || data.company_name || `Distributor #${item.entity_id}`;
      case 'company_product':
        return data.tile_code || data.name || `Product #${item.entity_id}`;
      case 'manufacturing_company':
        return data.company_name || data.company_code || `Company #${item.entity_id}`;
      default:
        return data.name || data.title || data.full_name || `ID: ${item.entity_id}`;
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';
  const totalPages = Math.ceil(total / limit);

  // Entity type options for filter
  const entityTypes = [
    'sales', 'sale_items', 'sale_comments', 'products', 'customers', 'referrers',
    'customer_invoices', 'bills', 'vendors', 'expenses', 'expense_categories',
    'journal_entries', 'journal_entry_lines', 'gl_accounts', 'brands', 'sizes',
    'use_cases', 'tile_types', 'optional_categories', 'optional_category_options',
    'tasks', 'users', 'wholesale_order', 'wholesale_distributor', 'company_product', 'manufacturing_company'
  ];

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Trash2 className="h-7 w-7 mr-2 text-gray-600" />
            Recycle Bin
          </h1>
          <p className="text-sm text-gray-500">Manage deleted items and restore data (Module: {effectiveModule})</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_items || 0}</p>
              </div>
              <BarChart3 className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-2 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Eligible for Purge</p>
                <p className="text-2xl font-bold text-gray-900">{stats.eligible_for_purge || 0}</p>
              </div>
              <Trash2 className="h-10 w-10 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-2 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entity Types</p>
                <p className="text-2xl font-bold text-gray-900">{Object.keys(stats.by_entity_type || {}).length}</p>
              </div>
              <Filter className="h-10 w-10 text-green-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <Filter className="h-5 w-5 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {showFilters && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Entity Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entity Type
                </label>
                <select
                  value={filters.entity_type}
                  onChange={(e) => {
                    setFilters({ ...filters, entity_type: e.target.value });
                    setCurrentPage(1);
                  }}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Types</option>
                  {entityTypes.map(type => (
                    <option key={type} value={type}>{formatEntityType(type)}</option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => {
                    setFilters({ ...filters, start_date: e.target.value });
                    setCurrentPage(1);
                  }}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => {
                    setFilters({ ...filters, end_date: e.target.value });
                    setCurrentPage(1);
                  }}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => {
                    setFilters({ ...filters, search: e.target.value });
                    setCurrentPage(1);
                  }}
                  placeholder="Search by entity ID, reason, or deleted by..."
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pl-10"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Clear Filters */}
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setFilters({
                    entity_type: '',
                    search: '',
                    deleted_by: '',
                    start_date: '',
                    end_date: ''
                  });
                  setCurrentPage(1);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <Trash2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No deleted items</h3>
            <p className="mt-1 text-sm text-gray-500">The recycle bin is empty.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entity Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entity Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Deleted At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Deleted By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatEntityType(item.entity_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getEntityName(item)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.deleted_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.deleted_by_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {item.deletion_reason || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleRestore(item)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Restore"
                        >
                          <RotateCcw className="h-5 w-5" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handlePermanentDelete(item)}
                            className="text-red-600 hover:text-red-900"
                            title="Permanently Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, total)}</span> of{' '}
                    <span className="font-medium">{total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showRestoreModal && (
        <RestoreModal
          item={selectedItem}
          isOpen={showRestoreModal}
          onClose={() => {
            setShowRestoreModal(false);
            setSelectedItem(null);
          }}
          onSuccess={handleSuccess}
        />
      )}

      {showDeleteModal && (
        <PermanentDeleteModal
          item={selectedItem}
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedItem(null);
          }}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default RecycleBinPage;
